import { useLocation, useParams, Link } from 'react-router-dom';
import clsx from 'clsx';
import { ChevronRightIcon } from '@heroicons/react/24/outline';

import Typography from '~/shared/components/Typography';
import { useBreadcrumb, type BreadcrumbItem } from '~/shared/context/BreadcrumbContext';

import ProfileDropdown from './ProfileDropdown';
import { sidebarItems } from './common';

export default function Header({ sidebarWidth }: { sidebarWidth: string }) {
  const { pathname } = useLocation();
  const params = useParams();
  const { breadcrumbs: customBreadcrumbs } = useBreadcrumb();

  const rootItem = sidebarItems.find((item) => pathname.startsWith(item.pathname));
  const title = rootItem?.title;

  const generateBreadcrumb = (): BreadcrumbItem[] | null => {
    if (customBreadcrumbs) {
      return customBreadcrumbs;
    }

    const segments = pathname.split('/').filter(Boolean);

    const excludedRoutes = ['autoscale-rules', 'all-volumes'];
    if (segments.some((seg) => excludedRoutes.includes(seg))) {
      return null;
    }

    const isSettingsRoute = pathname.startsWith('/settings');
    const isSettingsSubRoute =
      isSettingsRoute && (segments.includes('configuration') || segments.includes('permissions'));

    const isNestedPage =
      segments.length > 2 || Object.keys(params).length > 0 || isSettingsSubRoute;

    if (!isNestedPage) {
      return null;
    }

    const breadcrumbItems: BreadcrumbItem[] = [];

    if (title && rootItem) {
      breadcrumbItems.push({
        label: title,
        path: rootItem.pathname,
      });
    }
    const volumeId =
      params.id ||
      (pathname.startsWith('/fleet-manager') && segments.length >= 2
        ? segments[segments.length - 1]
        : params['*']);
    const looksLikeVolumeId = volumeId?.toLowerCase().startsWith('vol-') ?? false;
    const isFleetManagerVolumePage =
      pathname.startsWith('/fleet-manager') &&
      volumeId &&
      looksLikeVolumeId &&
      !['autoscale-rules', 'all-volumes'].includes(volumeId);

    if (volumeId && isFleetManagerVolumePage) {
      const cleanId = volumeId.replace(/^vol[-\s]*/i, '').trim();
      breadcrumbItems.push({
        label: `vol-${cleanId.toLowerCase()}`,
      });
    } else if (params.id) {
      breadcrumbItems.push({ label: params.id });
    } else {
      const subSegments = segments.slice(1);
      for (let i = 0; i < subSegments.length; i++) {
        const seg = subSegments[i];
        const label = seg
          .split('-')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');

        const path = `/${segments.slice(0, i + 2).join('/')}`;

        breadcrumbItems.push({
          label,
          path: i < subSegments.length - 1 ? path : undefined,
        });
      }
    }

    return breadcrumbItems;
  };

  const breadcrumbItems = generateBreadcrumb();

  return (
    <div
      className={clsx(
        'px-[30px] pr-[30px] min-h-[60px] 2xl:min-h-[64px] 4xl:min-h-[96px] w-full',
        'flex justify-between items-center bg-white z-10',
        'border-b border-primary-70 fixed top-0 right-0',
        'transition-all duration-300 ease-in-out'
      )}
      style={{
        left: sidebarWidth,
        width: `calc(100% - ${sidebarWidth})`,
      }}
    >
      {breadcrumbItems && breadcrumbItems.length > 0 ? (
        <nav aria-label="Breadcrumb" className="flex items-center gap-2">
          {breadcrumbItems.map((item, index) => {
            const isLast = index === breadcrumbItems.length - 1;
            return (
              <div key={index} className="flex items-center gap-2">
                <div className="flex items-center gap-3">
                  {item.path && !isLast ? (
                    <Link to={item.path} className="hover:opacity-70 transition-opacity">
                      <Typography
                        variant="H3-resp"
                        fontWeight={500}
                        color="text-gray-600 font-family-outfit"
                      >
                        {item.label}
                      </Typography>
                    </Link>
                  ) : (
                    <Typography
                      variant="H3-resp"
                      fontWeight={500}
                      color={
                        isLast
                          ? 'text-primary-900 font-family-outfit'
                          : 'text-gray-600 font-family-outfit'
                      }
                    >
                      {item.label}
                    </Typography>
                  )}
                  {item.suffix}
                </div>
                {!isLast && <ChevronRightIcon className="w-4 h-4 text-gray-600 stroke-3" />}
              </div>
            );
          })}
        </nav>
      ) : (
        <Typography variant="H3-resp" fontWeight={500} color="text-primary-900 font-family-outfit">
          {title || null}
        </Typography>
      )}

      <ProfileDropdown />
    </div>
  );
}
